import React from 'react';
import { TailwindModal } from './Tailwind';
import snakeToPascal from '../tools';

export interface ErrorItem {
    itemId: string;
    itemName: string;
    fieldName: string;
    errorMessage: string;
    type?: 'compute' | 'input';
    kind?: string;
    source?: string;
}

interface ErrorsModalProps {
    isOpen: boolean;
    onClose: () => void;
    errors: ErrorItem[];
    onErrorClick: (error: ErrorItem) => void;
    title?: string;
}

export default function ErrorsModal({ isOpen, onClose, errors, onErrorClick, title }: ErrorsModalProps) {
    const handleClick = (error: ErrorItem) => {
        onErrorClick(error);
        onClose();
    };

    return (
        <TailwindModal
            isOpen={isOpen}
            onClose={onClose}
            title={title || `Scenario Errors (${errors.length})`}
            size="lg"
        >
            {errors.length === 0 ? (
                <p className="text-sm text-slate-500 text-center py-4">No errors found.</p>
            ) : (
                <div className="max-h-[400px] overflow-y-auto -mx-2">
                    {errors.map((error, index) => (
                        <button
                            key={`${error.itemId}-${error.fieldName}-${index}`}
                            onClick={() => handleClick(error)}
                            className="w-full text-left px-3 py-2.5 hover:bg-slate-50 rounded-lg transition-colors flex items-start gap-3 group"
                        >
                            <span className={`material-symbols-outlined text-[18px] mt-0.5 shrink-0 ${error.type === 'input' ? 'text-amber-500' : 'text-red-500'}`}>
                                {error.type === 'input' ? 'info' : 'error'}
                            </span>
                            <div className="min-w-0 flex-1">
                                <div className="flex items-baseline gap-1.5">
                                    <span className="text-sm font-medium text-slate-900 truncate">
                                        {error.itemName}
                                    </span>
                                    {error.fieldName && (
                                        <span className="text-xs text-slate-400">
                                            / {snakeToPascal(error.fieldName)}
                                        </span>
                                    )}
                                </div>
                                <p className={`text-xs mt-0.5 leading-relaxed ${error.type === 'input' ? 'text-amber-600' : 'text-red-600'}`}>
                                    {error.errorMessage}
                                </p>
                            </div>
                            <span className="material-symbols-outlined text-[16px] text-slate-300 group-hover:text-slate-500 mt-0.5 shrink-0 transition-colors">
                                arrow_forward
                            </span>
                        </button>
                    ))}
                </div>
            )}
        </TailwindModal>
    );
}
